// File: src/components/FileUploader.tsx
// A reusable drag-and-drop file uploader component.
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, Paper, IconButton } from '@mui/material';
import { UploadFile as UploadFileIcon, Description as DescriptionIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface FileUploaderProps {
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (file: File) => void;
  acceptedFileTypes: { [key: string]: string[] };
  allowMultiple: boolean;
  title: string;
  description: string;
  files: File[];
}

export default function FileUploader({ onFilesSelected, onFileRemove, acceptedFileTypes, allowMultiple, title, description, files }: FileUploaderProps): JSX.Element {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!allowMultiple) {
        onFilesSelected(acceptedFiles);
    } else {
        onFilesSelected(acceptedFiles);
    }
  }, [onFilesSelected, allowMultiple]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: allowMultiple,
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'grey.50',
          transition: 'border-color 0.3s, background-color 0.3s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 180,
        }}
      >
        <input {...getInputProps()} />
        <UploadFileIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
        <Typography variant="h6" color="text.primary">
          {isDragActive ? 'Drop the files here...' : "Drag & drop or click to upload"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      {files.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Selected files:</Typography>
          <List dense>
            {files.map((file, index) => (
              <ListItem 
                key={index}
                secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => onFileRemove(file)} sx={{ "&:hover": { color: 'error.main' }}}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemIcon><DescriptionIcon /></ListItemIcon>
                <ListItemText 
                  primary={file.name} 
                  secondary={`${(file.size / 1024).toFixed(2)} KB`} 
                  primaryTypographyProps={{ style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
}